import React, { useState } from 'react';
import { useNavigate } from 'react-router-dom';
import { Heading, Text, Card, Section, PageHeading, PlainTable, PopupWindow, Tab } from '../components/ui';
import AdvancedPricingTiers from '../components/AdvancedPricingTiers';
import PixelArtLoader from '../components/PixelArtLoader';
import WalkthroughManager from '../components/WalkthroughManager';
import HeaderToggleTab from '../components/HeaderToggleTab';
import useHeaderToggle from '../hooks/useHeaderToggle';

const StyleGuide = () => {
  const navigate = useNavigate();
  const [showPopup, setShowPopup] = useState(false);
  const [selectedGoals, setSelectedGoals] = useState([]);
  const [selectedIngredients, setSelectedIngredients] = useState([]);
  const [showWalkthrough, setShowWalkthrough] = useState(false);
  const [walkthroughStep, setWalkthroughStep] = useState(0);
  const [currentPage, setCurrentPage] = useState('recipes'); // 'recipes' or 'create'
  const [demoTabVisible, setDemoTabVisible] = useState(true);
  const { isHeaderVisible, toggleHeader } = useHeaderToggle();

  const colorTokens = {
    alabaster: '#EEEEE7',
    white: '#FFFFFF',
    oxfordBlue: '#1b263b',
    richBlack: '#0d1b2a',
    neutral: {
      0: '#FFFFFF',
      50: '#F9F8F6',
      900: '#0D0D0D'
    }
  };

  const ColorSwatch = ({ color, name, value }) => (
    <div className="flex items-center space-x-4 p-3 bg-neutral-0 hairline">
      <div 
        className="w-12 h-12 hairline"
        style={{ backgroundColor: value }}
      />
      <div>
        <Text size="sm" className="font-mono font-medium">{name}</Text>
        <Text size="sm" className="text-neutral-900 font-mono">{value}</Text>
      </div>
    </div>
  );

  const ButtonDemo = ({ variant, children, ...props }) => {
    const baseClasses = "px-4 py-2 font-mono text-sm uppercase tracking-wider transition-all border-none";
    
    const variants = {
      primary: "text-white hover:opacity-60",
      secondary: "bg-neutral-50 text-neutral-900 hairline hover:bg-neutral-900 hover:text-neutral-0",
      small: "px-2 py-1 text-xs text-white hover:opacity-60",
      grid: "p-3 text-left text-xs bg-neutral-50 text-neutral-900 hairline hover:bg-neutral-900 hover:text-neutral-0"
    };

    const getBackgroundColor = () => {
      if (variant === 'primary' || variant === 'small') {
        return { backgroundColor: colorTokens.oxfordBlue };
      }
      return {};
    };

    return (
      <button 
        className={`${baseClasses} ${variants[variant]}`}
        style={getBackgroundColor()}
        {...props}
      >
        {children}
      </button>
    );
  };

  const exampleGoals = [
    "Quick weeknight dinner",
    "Weekend comfort food",
    "Healthy family meal",
    "Date night special"
  ];

  const exampleIngredients = [
    "chicken breast", "onions", "garlic", "olive oil", "pasta", "cheese"
  ];

  const toggleGoal = (goal) => {
    setSelectedGoals(prev => 
      prev.includes(goal) 
        ? prev.filter(g => g !== goal)
        : [...prev, goal]
    );
  };

  const toggleIngredient = (ingredient) => {
    setSelectedIngredients(prev => 
      prev.includes(ingredient) 
        ? prev.filter(i => i !== ingredient)
        : [...prev, ingredient]
    );
  };

  return (
    <div className="min-h-screen bg-neutral-50 px-0">
      <div className="w-full py-8">
        {/* Hero Section */}
        <div className="mb-8">
          <PageHeading size="hero" className="mb-4">4RKED DESIGN SYSTEM</PageHeading>
          <div className="bg-neutral-0 hairline minimal-padding">
            <Text size="lg" className="text-neutral-900 font-mono uppercase tracking-wider">
              Modern cooking platform with clean typography, purposeful design, and intuitive blueprint-based recipe management.
            </Text>
          </div>
        </div>

        {/* Brand Identity */}
        <div className="bg-neutral-0 hairline minimal-padding vspace mb-2">
          <Heading level={2} className="text-neutral-900" showInterLine style={{ color: colorTokens.oxfordBlue }}>Brand Identity</Heading>
          
          <Text className="text-neutral-900">
            The 4RKED brand represents a modern approach to cooking with clean, purposeful design that prioritizes clarity and user experience.
          </Text>
        </div>

        <div className="bg-neutral-0 hairline minimal-padding vspace mb-8">
          <div className="grid grid-cols-1 lg:grid-cols-2 gap-8">
            {/* Logo Section */}
            <div>
              <Heading level={4} className="text-neutral-900">Logo & Branding</Heading>
              <div className="bg-neutral-50 p-4 hairline mb-4">
                <div className="flex items-center space-x-4 mb-4">
                  <img 
                    src="/4.svg" 
                    alt="4RKED Logo" 
                    className="w-16 h-16"
                    style={{ filter: 'invert(1)' }}
                  />
                  <div>
                    <div className="text-2xl font-mono font-bold text-neutral-900 mb-1">4RKED</div>
                    <Text size="sm" className="text-neutral-900 opacity-60">
                      Primary logo mark with geometric design
                    </Text>
                  </div>
                </div>
              </div>
              
              <div className="bg-neutral-50 p-3 hairline">
                <Text size="sm" className="font-mono mb-2">LOGO SPECIFICATIONS:</Text>
                <ul className="list-disc list-inside space-y-1 text-xs text-neutral-900">
                  <li>Format: SVG (scalable) and PNG (raster)</li>
                  <li>Design: Geometric "4!" symbol in square frame</li>
                  <li>Color: White on dark backgrounds, Oxford Blue on light</li>
                  <li>Minimum size: 24px</li>
                  <li>Clear space: 1x height of logo</li>
                  <li>Usage: Primary brand mark for all applications</li>
                </ul>
              </div>
            </div>

            {/* Color Palette */}
            <div>
              <Heading level={4} className="text-neutral-900">Color Palette</Heading>
              <div className="space-y-0">
                <ColorSwatch name="alabaster" value={colorTokens.alabaster} />
                <ColorSwatch name="white" value={colorTokens.white} />
                <ColorSwatch name="oxford-blue" value={colorTokens.oxfordBlue} />
                <ColorSwatch name="rich-black" value={colorTokens.richBlack} />
              </div>
            </div>
          </div>
        </div>

        {/* Design Principles */}
        <div className="bg-neutral-0 hairline minimal-padding vspace mb-2">
          <Heading level={2} className="text-neutral-900" showInterLine style={{ color: colorTokens.oxfordBlue }}>Design Principles</Heading>
          
          <Text className="text-neutral-900">
            Our design system is built on five core principles that guide every interface decision, ensuring a cohesive and purposeful user experience.
          </Text>
        </div>

        <div className="bg-neutral-0 hairline minimal-padding vspace mb-8">
          <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-3 gap-6">
            <div className="bg-neutral-50 p-4 hairline">
              <Heading level={4} className="text-neutral-900 mb-2">Clarity</Heading>
              <Text size="sm" className="text-neutral-900">
                Information should be presented in a clean, easily scannable format. Every element serves a clear purpose and communicates its function immediately.
              </Text>
            </div>
            <div className="bg-neutral-50 p-4 hairline">
              <Heading level={4} className="text-neutral-900 mb-2">Consistency</Heading>
              <Text size="sm" className="text-neutral-900">
                UI elements maintain visual coherence throughout the application. Similar functions use similar patterns and styling.
              </Text>
            </div>
            <div className="bg-neutral-50 p-4 hairline">
              <Heading level={4} className="text-neutral-900 mb-2">Hierarchy</Heading>
              <Text size="sm" className="text-neutral-900">
                Important information and actions are visually prioritized through typography, spacing, and color to guide user attention.
              </Text>
            </div>
            <div className="bg-neutral-50 p-4 hairline">
              <Heading level={4} className="text-neutral-900 mb-2">Accessibility</Heading>
              <Text size="sm" className="text-neutral-900">
                All designs meet WCAG AA standards with proper contrast ratios, keyboard navigation, and screen reader compatibility.
              </Text>
            </div>
            <div className="bg-neutral-50 p-4 hairline">
              <Heading level={4} className="text-neutral-900 mb-2">Purposeful</Heading>
              <Text size="sm" className="text-neutral-900">
                Every design element serves a specific function. Decorative elements are minimized in favor of functional clarity.
              </Text>
            </div>
          </div>
        </div>

        {/* Component Library */}
        <div className="bg-neutral-0 hairline minimal-padding vspace mb-2">
          <Heading level={2} className="text-neutral-900" showInterLine style={{ color: colorTokens.oxfordBlue }}>Component Library</Heading>
          
          <Text className="text-neutral-900">
            A comprehensive collection of interface components designed for the 4RKED cooking platform, emphasizing clarity and user experience.
          </Text>
        </div>

        <div className="bg-neutral-0 hairline minimal-padding vspace mb-8">
          <div className="grid grid-cols-1 lg:grid-cols-2 gap-8">
            {/* Popup Window Demo */}
            <div>
              <Heading level={4} className="text-neutral-900">Popup Windows</Heading>
              <Text size="sm" className="text-neutral-900 mb-4">
                Draggable windows with retro styling and square borders. Click to open demo:
              </Text>
              
              <ButtonDemo 
                variant="primary"
                onClick={() => setShowPopup(true)}
              >
                Open Popup Demo
              </ButtonDemo>

              <div className="mt-4 bg-neutral-50 p-3 hairline">
                <Text size="sm" className="font-mono mb-2">FEATURES:</Text>
                <ul className="list-disc list-inside space-y-1 text-xs text-neutral-900">
                  <li>Draggable from title bar</li>
                  <li>Rigid shadow styling</li>
                  <li>Vertical scrolling content</li>
                  <li>ESC key support</li>
                  <li>Stacking with offset</li>
                </ul>
              </div>
            </div>

            {/* Blueprint Interface Demo */}
            <div>
              <Heading level={4} className="text-neutral-900">Blueprint Interface</Heading>
              <Text size="sm" className="text-neutral-900 mb-4">
                Full-screen overlay modals with blueprint-specific content and grid-based selection patterns for recipe management.
              </Text>
              
              <div className="bg-neutral-50 p-3 hairline">
                <Text size="sm" className="font-mono mb-2">COOK NOW MODAL FEATURES:</Text>
                <ul className="list-disc list-inside space-y-1 text-xs text-neutral-900">
                  <li>Blueprint-specific meal goals</li>
                  <li>Ingredient selection grids</li>
                  <li>X-button only closing</li>
                  <li>No outside-click dismissal</li>
                  <li>Progress states</li>
                </ul>
              </div>
            </div>
          </div>
        </div>

        {/* Modal System */}
        <div className="bg-neutral-0 hairline minimal-padding vspace mb-2">
          <Heading level={2} className="text-neutral-900" showInterLine>Modal System</Heading>
          
          <Text className="text-neutral-900">
            Consistent modal patterns with clean close buttons, scroll prevention, and responsive sizing. All modals use the same overlay and interaction patterns.
          </Text>
        </div>

        <div className="bg-neutral-0 hairline minimal-padding vspace mb-8">
          <div className="grid grid-cols-1 lg:grid-cols-2 gap-8">
            {/* Close Button Pattern */}
            <div>
              <Heading level={4} className="text-neutral-900">Close Button Design</Heading>
              <Text size="sm" className="text-neutral-900 mb-4">
                Clean, minimal close buttons with no borders or background. Consistent across all modals and popups.
              </Text>
              
              <div className="bg-neutral-50 p-4 hairline mb-4">
                <div className="flex items-center justify-between">
                  <Text size="sm" className="font-mono text-neutral-900">Modal Title</Text>
                  <button className="text-neutral-900 hover:opacity-60 transition-opacity duration-120 font-mono text-lg border-none bg-transparent p-0 m-0 modal-close">
                    ✕
                  </button>
                </div>
              </div>
              
              <div className="bg-neutral-50 p-3 hairline">
                <Text size="sm" className="font-mono mb-2">CLOSE BUTTON FEATURES:</Text>
                <ul className="list-disc list-inside space-y-1 text-xs text-neutral-900">
                  <li>X symbol only (no "Close" text)</li>
                  <li>No borders or background</li>
                  <li>Hover opacity effect</li>
                  <li>Overrides global button styles</li>
                  <li>Consistent positioning (top-right)</li>
                </ul>
              </div>
            </div>

            {/* Scroll Prevention */}
            <div>
              <Heading level={4} className="text-neutral-900">Scroll Prevention</Heading>
              <Text size="sm" className="text-neutral-900 mb-4">
                Automatic body scroll prevention when modals are open. Prevents user confusion and maintains focus.
              </Text>
              
              <div className="bg-neutral-50 p-3 hairline mb-4">
                <Text size="sm" className="font-mono mb-2">IMPLEMENTATION:</Text>
                <div className="text-xs text-neutral-900 font-mono">
                  useEffect(() =&gt; {'{'}
                  <br />
                  &nbsp;&nbsp;if (isOpen) {'{'}
                  <br />
                  &nbsp;&nbsp;&nbsp;&nbsp;document.body.classList.add('modal-open');
                  <br />
                  &nbsp;&nbsp;{'}'} else {'{'}
                  <br />
                  &nbsp;&nbsp;&nbsp;&nbsp;document.body.classList.remove('modal-open');
                  <br />
                  &nbsp;&nbsp;{'}'}
                  <br />
                  {'}'}, [isOpen]);
                </div>
              </div>
              
              <div className="bg-neutral-50 p-3 hairline">
                <Text size="sm" className="font-mono mb-2">CSS RULE:</Text>
                <Text size="xs" className="text-neutral-900 font-mono">
                  body.modal-open {'{'} overflow: hidden; {'}'}
                </Text>
              </div>
            </div>
          </div>
        </div>

        {/* Modal Sizing */}
        <div className="bg-neutral-0 hairline minimal-padding vspace mb-8">
          <Heading level={3} className="text-neutral-900">Modal Sizing Hierarchy</Heading>
          
          <div className="grid grid-cols-1 md:grid-cols-3 gap-6">
            <div className="bg-neutral-50 p-4 hairline">
              <Heading level={4} className="text-neutral-900 mb-2">Main Calendar</Heading>
              <Text size="sm" className="font-mono text-neutral-900 mb-2">max-w-4xl</Text>
              <Text size="xs" className="text-neutral-900 opacity-60">Largest modal for calendar view</Text>
            </div>
            
            <div className="bg-neutral-50 p-4 hairline">
              <Heading level={4} className="text-neutral-900 mb-2">Day Sessions</Heading>
              <Text size="sm" className="font-mono text-neutral-900 mb-2">max-w-3xl</Text>
              <Text size="xs" className="text-neutral-900 opacity-60">Medium for session lists</Text>
            </div>
            
            <div className="bg-neutral-50 p-4 hairline">
              <Heading level={4} className="text-neutral-900 mb-2">Session Details</Heading>
              <Text size="sm" className="font-mono text-neutral-900 mb-2">max-w-3xl</Text>
              <Text size="xs" className="text-neutral-900 opacity-60">Medium for detailed views</Text>
            </div>
          </div>
        </div>

        {/* Button System */}
        <div className="bg-neutral-0 hairline minimal-padding vspace mb-2">
          <Heading level={2} className="text-neutral-900" showInterLine>Button System</Heading>
          
          <Text className="text-neutral-900">
            Clean button hierarchy with proper contrast ratios and consistent interactive states. All buttons use font-mono and uppercase labels.
          </Text>
        </div>

        <div className="bg-neutral-0 hairline minimal-padding vspace mb-8">
          <div className="grid grid-cols-1 lg:grid-cols-2 gap-8">
            {/* Primary Buttons */}
            <div>
              <Heading level={4} className="text-neutral-900">Primary Actions</Heading>
              <div className="space-y-3 mb-4">
                <div className="flex items-center space-x-4">
                  <ButtonDemo variant="primary">Primary Button</ButtonDemo>
                  <Text size="sm" className="font-mono text-neutral-900">Oxford Blue background, white text</Text>
                </div>
                <div className="flex items-center space-x-4">
                  <ButtonDemo variant="small">Small Action</ButtonDemo>
                  <Text size="sm" className="font-mono text-neutral-900">Compact variant</Text>
                </div>
              </div>
              
              <div className="bg-neutral-50 p-3 hairline">
                <Text size="sm" className="font-mono mb-2">HOVER STATE:</Text>
                <Text size="xs" className="text-neutral-900">opacity-60 (maintains contrast)</Text>
              </div>
            </div>

            {/* Secondary Buttons */}
            <div>
              <Heading level={4} className="text-neutral-900">Secondary Actions</Heading>
              <div className="space-y-3 mb-4">
                <div className="flex items-center space-x-4">
                  <ButtonDemo variant="secondary">Secondary Button</ButtonDemo>
                  <Text size="sm" className="font-mono text-neutral-900">bg-neutral-50 with hairline border</Text>
                </div>
                <div className="flex items-center space-x-4">
                  <ButtonDemo variant="grid">Grid Selection</ButtonDemo>
                  <Text size="sm" className="font-mono text-neutral-900">bg-neutral-50 with hairline border</Text>
                </div>
              </div>
              
              <div className="bg-neutral-50 p-3 hairline">
                <Text size="sm" className="font-mono mb-2">HOVER STATE:</Text>
                <Text size="xs" className="text-neutral-900">bg-neutral-900 text-neutral-0 (inverts colors)</Text>
              </div>
            </div>
          </div>
        </div>

        {/* Recipe Cards */}
        <div className="bg-neutral-0 hairline minimal-padding vspace mb-2">
          <Heading level={2} className="text-neutral-900" showInterLine>Recipe Cards</Heading>
          
          <Text className="text-neutral-900">
            Clean, scannable recipe cards with improved visual hierarchy and clear action buttons. Designed for easy browsing and quick decision-making.
          </Text>
        </div>

        <div className="bg-neutral-0 hairline minimal-padding vspace mb-8">
          <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-3 gap-6">
            {/* Recipe Card Example */}
            <div className="bg-neutral-50 p-4 hairline">
              <div className="aspect-square bg-neutral-0 hairline mb-3 flex items-center justify-center">
                <Text size="sm" className="font-mono text-neutral-900 opacity-60">Recipe Image</Text>
              </div>
              <Heading level={4} className="text-neutral-900 mb-2">Classic Spaghetti Carbonara</Heading>
              <Text size="sm" className="text-neutral-900 mb-3 opacity-60">
                Traditional Italian pasta with eggs, cheese, and pancetta
              </Text>
              <div className="flex justify-between items-center">
                <Text size="xs" className="font-mono text-neutral-900">30 min</Text>
                <ButtonDemo variant="small">Cook Now</ButtonDemo>
              </div>
            </div>
            
            <div className="bg-neutral-50 p-4 hairline">
              <div className="aspect-square bg-neutral-0 hairline mb-3 flex items-center justify-center">
                <Text size="sm" className="font-mono text-neutral-900 opacity-60">Recipe Image</Text>
              </div>
              <Heading level={4} className="text-neutral-900 mb-2">Mediterranean Quinoa Bowl</Heading>
              <Text size="sm" className="text-neutral-900 mb-3 opacity-60">
                Healthy grain bowl with vegetables and tahini dressing
              </Text>
              <div className="flex justify-between items-center">
                <Text size="xs" className="font-mono text-neutral-900">25 min</Text>
                <ButtonDemo variant="small">Cook Now</ButtonDemo>
              </div>
            </div>
            
            <div className="bg-neutral-50 p-4 hairline">
              <div className="aspect-square bg-neutral-0 hairline mb-3 flex items-center justify-center">
                <Text size="sm" className="font-mono text-neutral-900 opacity-60">Recipe Image</Text>
              </div>
              <Heading level={4} className="text-neutral-900 mb-2">Chocolate Chip Cookies</Heading>
              <Text size="sm" className="text-neutral-900 mb-3 opacity-60">
                Soft and chewy cookies perfect for any occasion
              </Text>
              <div className="flex justify-between items-center">
                <Text size="xs" className="font-mono text-neutral-900">45 min</Text>
                <ButtonDemo variant="small">Cook Now</ButtonDemo>
              </div>
            </div>
          </div>
          
          <div className="bg-neutral-50 p-3 hairline mt-4">
            <Text size="sm" className="font-mono mb-2">RECIPE CARD FEATURES:</Text>
            <ul className="list-disc list-inside space-y-1 text-xs text-neutral-900">
              <li>Square aspect ratio for consistent grid layout</li>
              <li>Clear typography hierarchy with recipe title</li>
              <li>Brief description for context</li>
              <li>Cooking time prominently displayed</li>
              <li>Primary action button for immediate engagement</li>
              <li>Hover states for interactive feedback</li>
            </ul>
          </div>
        </div>

        {/* Selection Grids */}
        <div className="bg-neutral-0 hairline minimal-padding vspace mb-2">
          <Heading level={2} className="text-neutral-900" showInterLine>Selection Grids</Heading>
          
          <Text className="text-neutral-900">
            Interactive grid layouts for option selection. Used in Cook Now modal and other configuration interfaces.
          </Text>
        </div>

        <div className="bg-neutral-0 hairline minimal-padding vspace mb-8">
          <div className="space-y-6">
            {/* Meal Goals Grid */}
            <div>
              <div className="mb-3">
                <div className="font-mono uppercase text-sm tracking-wider text-neutral-900 mb-1">MEAL GOALS</div>
                <div className="text-xs text-neutral-900 opacity-60">Select cooking objectives (example grid)</div>
              </div>
              
              <div className="grid grid-cols-2 md:grid-cols-4 gap-2">
                {exampleGoals.map((goal, index) => (
                  <button
                    key={index}
                    onClick={() => toggleGoal(goal)}
                    className={`p-3 text-left text-xs font-mono hairline transition-colors ${
                      selectedGoals.includes(goal) 
                        ? 'bg-neutral-900 text-neutral-0 hover:bg-neutral-900' 
                        : 'bg-neutral-50 text-neutral-900 hover:bg-neutral-900 hover:text-neutral-0'
                    }`}
                  >
                    {goal}
                  </button>
                ))}
              </div>
            </div>

            {/* Ingredients Grid */}
            <div>
              <div className="mb-3">
                <div className="font-mono uppercase text-sm tracking-wider text-neutral-900 mb-1">INGREDIENTS</div>
                <div className="text-xs text-neutral-900 opacity-60">Select available ingredients (example grid)</div>
              </div>
              
              <div className="grid grid-cols-3 md:grid-cols-6 gap-2">
                {exampleIngredients.map((ingredient, index) => (
                  <button
                    key={index}
                    onClick={() => toggleIngredient(ingredient)}
                    className={`p-2 text-xs font-mono hairline transition-colors ${
                      selectedIngredients.includes(ingredient)
                        ? 'bg-neutral-900 text-neutral-0 hover:bg-neutral-900' 
                        : 'bg-neutral-50 text-neutral-900 hover:bg-neutral-900 hover:text-neutral-0'
                    }`}
                  >
                    {ingredient}
                  </button>
                ))}
              </div>

              {/* Selected Display */}
              {selectedIngredients.length > 0 && (
                <div className="mt-4 p-3 bg-neutral-50 hairline">
                  <div className="font-mono text-xs uppercase tracking-wider mb-2">SELECTED:</div>
                  <div className="flex flex-wrap gap-1">
                    {selectedIngredients.map((ingredient, index) => (
                      <span 
                        key={index}
                        className="px-2 py-1 bg-neutral-900 text-neutral-0 text-xs font-mono cursor-pointer hover:opacity-60"
                        onClick={() => toggleIngredient(ingredient)}
                      >
                        {ingredient} ✕
                      </span>
                    ))}
                  </div>
                </div>
              )}
            </div>
          </div>
        </div>

        {/* UI Guidelines */}
        <div className="bg-neutral-0 hairline minimal-padding vspace mb-2">
          <Heading level={2} className="text-neutral-900" showInterLine>UI Guidelines</Heading>
          
          <Text className="text-neutral-900">
            Comprehensive guidelines for spacing, layout, responsive behavior, and interactive states that ensure consistency across the platform.
          </Text>
        </div>

        <div className="bg-neutral-0 hairline minimal-padding vspace mb-8">
          <div className="grid grid-cols-1 lg:grid-cols-2 gap-8">
            {/* Spacing System */}
            <div>
              <Heading level={4} className="text-neutral-900">Spacing System</Heading>
              <div className="space-y-4 mb-4">
                <div className="bg-neutral-50 p-3 hairline">
                  <Text size="sm" className="font-mono mb-2">SPACING UNITS:</Text>
                  <div className="space-y-2 text-xs text-neutral-900">
                    <div className="flex justify-between">
                      <span>xs</span>
                      <span className="font-mono">4px</span>
                    </div>
                    <div className="flex justify-between">
                      <span>sm</span>
                      <span className="font-mono">8px</span>
                    </div>
                    <div className="flex justify-between">
                      <span>md</span>
                      <span className="font-mono">16px</span>
                    </div>
                    <div className="flex justify-between">
                      <span>lg</span>
                      <span className="font-mono">24px</span>
                    </div>
                    <div className="flex justify-between">
                      <span>xl</span>
                      <span className="font-mono">32px</span>
                    </div>
                    <div className="flex justify-between">
                      <span>2xl</span>
                      <span className="font-mono">48px</span>
                    </div>
                  </div>
                </div>
              </div>
            </div>

            {/* Grid System */}
            <div>
              <Heading level={4} className="text-neutral-900">Grid System</Heading>
              <div className="space-y-4 mb-4">
                <div className="bg-neutral-50 p-3 hairline">
                  <Text size="sm" className="font-mono mb-2">BREAKPOINTS:</Text>
                  <div className="space-y-2 text-xs text-neutral-900">
                    <div className="flex justify-between">
                      <span>Mobile</span>
                      <span className="font-mono">320px - 767px</span>
                    </div>
                    <div className="flex justify-between">
                      <span>Tablet</span>
                      <span className="font-mono">768px - 1023px</span>
                    </div>
                    <div className="flex justify-between">
                      <span>Desktop</span>
                      <span className="font-mono">1024px+</span>
                    </div>
                  </div>
                </div>
              </div>
            </div>

            {/* State Handling */}
            <div>
              <Heading level={4} className="text-neutral-900">State Handling</Heading>
              <div className="space-y-3">
                <div className="flex items-center space-x-4">
                  <ButtonDemo variant="primary">Default</ButtonDemo>
                  <Text size="sm" className="font-mono text-neutral-900">Normal state</Text>
                </div>
                <div className="flex items-center space-x-4">
                  <ButtonDemo variant="secondary">Hover</ButtonDemo>
                  <Text size="sm" className="font-mono text-neutral-900">Hover state</Text>
                </div>
                <div className="flex items-center space-x-4">
                  <button className="px-4 py-2 font-mono text-sm uppercase tracking-wider bg-neutral-50 text-neutral-900 hairline opacity-50 cursor-not-allowed">
                    Disabled
                  </button>
                  <Text size="sm" className="font-mono text-neutral-900">Disabled state</Text>
                </div>
              </div>
            </div>

            {/* Animation Principles */}
            <div>
              <Heading level={4} className="text-neutral-900">Animation Principles</Heading>
              <div className="bg-neutral-50 p-3 hairline">
                <Text size="sm" className="font-mono mb-2">TIMING & EASING:</Text>
                <ul className="list-disc list-inside space-y-1 text-xs text-neutral-900">
                  <li>Hover transitions: 120ms ease-in-out</li>
                  <li>Modal animations: 200ms ease-out</li>
                  <li>Page transitions: 300ms ease-in-out</li>
                  <li>Loading states: 400ms ease-in-out</li>
                  <li>Use sparingly for functional feedback</li>
                </ul>
              </div>
            </div>
          </div>
        </div>

        {/* Typography Scale */}
        <div className="bg-neutral-0 hairline minimal-padding vspace mb-8">
          <Heading level={2} className="text-neutral-900" showInterLine>Typography Scale</Heading>
          <div className="vspace">
            <div className="space-y-6">
              <div>
                <PageHeading size="hero">HERO SCALE</PageHeading>
                <Text className="text-neutral-900 font-mono text-sm mt-2 opacity-60">
                  font-sans text-[6.4rem] leading-[0.85] font-medium (reduced by 20%)
                </Text>
              </div>
              <div>
                <Heading level={1} className="text-neutral-900">H1 PRIMARY HEADING</Heading>
                <Text className="text-neutral-900 font-mono text-sm mt-2 opacity-60">
                  font-sans text-4xl font-medium (reduced by 20%)
                </Text>
              </div>
              <div>
                <Heading level={2} className="text-neutral-900">H2 Section Heading</Heading>
                <Text className="text-neutral-900 font-mono text-sm mt-2 opacity-60">
                  font-sans text-2xl font-medium (reduced by 20%)
                </Text>
              </div>
              <div>
                <Heading level={3} className="text-neutral-900">H3 Subsection</Heading>
                <Text className="text-neutral-900 font-mono text-sm mt-2 opacity-60">
                  font-sans text-lg font-medium (reduced by 20%)
                </Text>
              </div>
              <div>
                <Text size="base" className="text-neutral-900">Body text — clean, readable copy for content and interface descriptions</Text>
                <Text className="text-neutral-900 font-mono text-sm mt-2 opacity-60">
                  font-sans text-base leading-relaxed
                </Text>
              </div>
              <div>
                <Text size="sm" className="text-neutral-900 font-mono uppercase tracking-wider">
                  METADATA • LABELS • CATEGORIES
                </Text>
                <Text className="text-neutral-900 font-mono text-sm mt-2 opacity-60">
                  font-mono text-sm uppercase tracking-wider
                </Text>
              </div>
            </div>
          </div>
        </div>

        {/* Font Families */}
        <div className="bg-neutral-0 hairline minimal-padding vspace mb-8">
          <Heading level={2} className="text-neutral-900" showInterLine>Font Families</Heading>
          
          <div className="grid grid-cols-1 lg:grid-cols-2 gap-8">
            {/* Primary Fonts */}
            <div>
              <Heading level={4} className="text-neutral-900">Primary Font Stack</Heading>
              <div className="space-y-4">
                <div className="bg-neutral-50 p-4 hairline">
                  <div className="font-mono text-sm uppercase tracking-wider text-neutral-900 mb-2">COURIER PRIME (MONOSPACE)</div>
                  <div className="font-mono text-lg text-neutral-900 mb-2">ABCDEFGHIJKLMNOPQRSTUVWXYZ</div>
                  <div className="font-mono text-lg text-neutral-900 mb-2">abcdefghijklmnopqrstuvwxyz</div>
                  <div className="font-mono text-lg text-neutral-900 mb-2">0123456789</div>
                  <Text size="sm" className="text-neutral-900 opacity-60">
                    Used for: Labels, buttons, metadata, timers, navigation, step counters
                  </Text>
                  <Text size="sm" className="text-neutral-900 opacity-60 font-mono">
                    Class: font-mono
                  </Text>
                </div>
                
                <div className="bg-neutral-50 p-4 hairline">
                  <div className="font-sans text-sm uppercase tracking-wider text-neutral-900 mb-2">INTER (SANS SERIF)</div>
                  <div className="font-sans text-lg text-neutral-900 mb-2">ABCDEFGHIJKLMNOPQRSTUVWXYZ</div>
                  <div className="font-sans text-lg text-neutral-900 mb-2">abcdefghijklmnopqrstuvwxyz</div>
                  <div className="font-sans text-lg text-neutral-900 mb-2">0123456789</div>
                  <Text size="sm" className="text-neutral-900 opacity-60">
                    Used for: Body text, headings, general content
                  </Text>
                  <Text size="sm" className="text-neutral-900 opacity-60 font-mono">
                    Class: font-sans (default)
                  </Text>
                </div>
                
                <div className="bg-neutral-50 p-4 hairline">
                  <div className="font-serif text-sm uppercase tracking-wider text-neutral-900 mb-2">IBM PLEX SERIF</div>
                  <div className="font-serif text-lg text-neutral-900 mb-2">ABCDEFGHIJKLMNOPQRSTUVWXYZ</div>
                  <div className="font-serif text-lg text-neutral-900 mb-2">abcdefghijklmnopqrstuvwxyz</div>
                  <div className="font-serif text-lg text-neutral-900 mb-2">0123456789</div>
                  <Text size="sm" className="text-neutral-900 opacity-60">
                    Used for: Serif headings and titles
                  </Text>
                  <Text size="sm" className="text-neutral-900 opacity-60 font-mono">
                    Class: font-serif
                  </Text>
                </div>
              </div>
            </div>

            {/* Font Configuration */}
            <div>
              <Heading level={4} className="text-neutral-900">Font Configuration</Heading>
              <Text className="text-neutral-900 mb-4">
                Fonts are loaded from Google Fonts and configured in Tailwind CSS. The monospace font (Courier Prime) is the most dominant, giving the app its distinctive editorial aesthetic.
              </Text>
              
              <div className="bg-neutral-50 p-4 hairline mb-4">
                <Text size="sm" className="font-mono mb-2">TAILWIND CONFIG:</Text>
                <Text size="xs" className="text-neutral-900 font-mono">
                  fontFamily: {'{'}
                </Text>
                <Text size="xs" className="text-neutral-900 font-mono ml-4">
                  sans: ['Inter', ...defaultTheme.fontFamily.sans],
                </Text>
                <Text size="xs" className="text-neutral-900 font-mono ml-4">
                  mono: ['"Courier Prime"', ...defaultTheme.fontFamily.mono],
                </Text>
                <Text size="xs" className="text-neutral-900 font-mono ml-4">
                  serif: ['"IBM Plex Serif"', ...defaultTheme.fontFamily.serif]
                </Text>
                <Text size="xs" className="text-neutral-900 font-mono">
                  {'}'}
                </Text>
              </div>
              
              <div className="bg-neutral-50 p-4 hairline">
                <Text size="sm" className="font-mono mb-2">GOOGLE FONTS IMPORT:</Text>
                <Text size="xs" className="text-neutral-900 font-mono">
                  @import url("https://fonts.googleapis.com/css2?family=Playfair+Display:wght@400;600;700&family=Lato:wght@300;400;700&family=Fredoka+One&family=Bungee&display=swap");
                </Text>
              </div>
            </div>
          </div>
        </div>

        {/* Color Palette */}
        <div className="bg-neutral-0 hairline minimal-padding vspace mb-8">
          <Heading level={2} className="text-neutral-900" showInterLine>Color Palette</Heading>
          
          <div className="grid grid-cols-1 lg:grid-cols-2 gap-8">
            {/* Primary Colors */}
            <div>
              <Heading level={4} className="text-neutral-900">Primary Colors</Heading>
              <div className="space-y-0">
                <ColorSwatch name="alabaster" value={colorTokens.alabaster} />
                <ColorSwatch name="white" value={colorTokens.white} />
                <ColorSwatch name="oxford-blue" value={colorTokens.oxfordBlue} />
                <ColorSwatch name="rich-black" value={colorTokens.richBlack} />
              </div>
            </div>

            {/* Color Usage */}
            <div>
              <Heading level={4} className="text-neutral-900">Color Usage</Heading>
              <div className="space-y-4">
                <div className="bg-neutral-50 p-3 hairline">
                  <Text size="sm" className="font-mono mb-2">BACKGROUNDS:</Text>
                  <ul className="list-disc list-inside space-y-1 text-xs text-neutral-900">
                    <li>Alabaster (#EEEEE7) - Primary page background</li>
                    <li>White (#FFFFFF) - Content containers and cards</li>
                  </ul>
                </div>
                
                <div className="bg-neutral-50 p-3 hairline">
                  <Text size="sm" className="font-mono mb-2">TEXT & BORDERS:</Text>
                  <ul className="list-disc list-inside space-y-1 text-xs text-neutral-900">
                    <li>Oxford Blue (#1b263b) - Primary text, headings, and primary buttons</li>
                    <li>Rich Black (#0d1b2a) - Secondary text and high-contrast elements</li>
                  </ul>
                </div>
                
                <div className="bg-neutral-50 p-3 hairline">
                  <Text size="sm" className="font-mono mb-2">OXFORD BLUE USAGE:</Text>
                  <ul className="list-disc list-inside space-y-1 text-xs text-neutral-900">
                    <li>Primary buttons and call-to-action elements</li>
                    <li>Main headings and section titles</li>
                    <li>Navigation elements and links</li>
                    <li>Form labels and important text</li>
                    <li>Logo on light backgrounds</li>
                    <li>Border accents for emphasis</li>
                  </ul>
                </div>
                
                <div className="bg-neutral-50 p-3 hairline">
                  <Text size="sm" className="font-mono mb-2">LEGACY COLORS:</Text>
                  <ul className="list-disc list-inside space-y-1 text-xs text-neutral-900">
                    <li>Neutral-50 (#F9F8F6) - Legacy background</li>
                    <li>Neutral-900 (#0D0D0D) - Legacy text color</li>
                  </ul>
                </div>
              </div>
            </div>
          </div>
        </div>

        {/* Shadow System */}
        <div className="bg-neutral-0 hairline minimal-padding vspace mb-8">
          <Heading level={2} className="text-neutral-900" showInterLine>Shadow System</Heading>
          
          <div className="grid grid-cols-1 md:grid-cols-3 gap-6">
            <div>
              <Heading level={4} className="text-neutral-900">Popup Shadow</Heading>
              <div className="w-24 h-24 bg-neutral-0 hairline shadow-popup flex items-center justify-center border border-black">
                <Text size="sm" className="font-mono">POPUP</Text>
              </div>
              <Text size="sm" className="font-mono mt-2 text-neutral-900 opacity-60">
                4px 4px 0 rgba(0,0,0,0.25)
              </Text>
            </div>
            
            <div>
              <Heading level={4} className="text-neutral-900">Hairline Border</Heading>
              <div className="w-24 h-24 bg-neutral-0 hairline flex items-center justify-center">
                <Text size="sm" className="font-mono">BORDER</Text>
              </div>
              <Text size="sm" className="font-mono mt-2 text-neutral-900 opacity-60">
                1px solid rgba(13,13,13,0.9)
              </Text>
            </div>

            <div>
              <Heading level={4} className="text-neutral-900">Container</Heading>
              <div className="w-24 h-24 bg-neutral-50 hairline flex items-center justify-center">
                <Text size="sm" className="font-mono">BG-50</Text>
              </div>
              <Text size="sm" className="font-mono mt-2 text-neutral-900 opacity-60">
                Neutral-50 background
              </Text>
            </div>
          </div>
        </div>

        {/* Layout System */}
        <div className="bg-neutral-0 hairline minimal-padding vspace mb-8">
          <Heading level={2} className="text-neutral-900" showInterLine>Layout System</Heading>
          
          <Text className="text-neutral-900 mb-6">
            Edge-to-edge grid system with zero horizontal padding and generous vertical spacing. Content density is maximized while maintaining clear hierarchy.
          </Text>

          <div className="bg-neutral-50 hairline p-0">
            <PlainTable>
              <thead>
                <PlainTable.Row>
                  <PlainTable.Cell header>Component</PlainTable.Cell>
                  <PlainTable.Cell header>Spacing</PlainTable.Cell>
                  <PlainTable.Cell header>Usage</PlainTable.Cell>
                </PlainTable.Row>
              </thead>
              <tbody>
                <PlainTable.Row>
                  <PlainTable.Cell>Page Container</PlainTable.Cell>
                  <PlainTable.Cell>
                    <span className="font-mono text-sm">px-2 sm:px-3 md:px-4</span>
                  </PlainTable.Cell>
                  <PlainTable.Cell>Slight horizontal padding (8px mobile, 12px small, 16px medium+)</PlainTable.Cell>
                </PlainTable.Row>
                <PlainTable.Row>
                  <PlainTable.Cell>Content Container</PlainTable.Cell>
                  <PlainTable.Cell>
                    <span className="font-mono text-sm">minimal-padding</span>
                  </PlainTable.Cell>
                  <PlainTable.Cell>White container internal spacing</PlainTable.Cell>
                </PlainTable.Row>
                <PlainTable.Row>
                  <PlainTable.Cell>Modal Interface</PlainTable.Cell>
                  <PlainTable.Cell>
                    <span className="font-mono text-sm">p-6</span>
                  </PlainTable.Cell>
                  <PlainTable.Cell>Generous modal padding</PlainTable.Cell>
                </PlainTable.Row>
                <PlainTable.Row>
                  <PlainTable.Cell>Grid Selections</PlainTable.Cell>
                  <PlainTable.Cell>
                    <span className="font-mono text-sm">gap-2</span>
                  </PlainTable.Cell>
                  <PlainTable.Cell>Tight grid spacing</PlainTable.Cell>
                </PlainTable.Row>
                <PlainTable.Row>
                  <PlainTable.Cell>Vertical Rhythm</PlainTable.Cell>
                  <PlainTable.Cell>
                    <span className="font-mono text-sm">vspace / space-y-8</span>
                  </PlainTable.Cell>
                  <PlainTable.Cell>Generous content spacing</PlainTable.Cell>
                </PlainTable.Row>
              </tbody>
            </PlainTable>
          </div>
        </div>

        {/* Checkbox System */}
        <div className="bg-neutral-0 hairline minimal-padding vspace mb-2">
          <Heading level={2} className="text-neutral-900" showInterLine>Checkbox System</Heading>
          
          <Text className="text-neutral-900">
            Square checkboxes with consistent styling across all interfaces. All checkboxes use the same base styling with square corners and monochrome gray color scheme.
          </Text>
        </div>

        <div className="bg-neutral-0 hairline minimal-padding vspace mb-8">
          <div className="grid grid-cols-1 lg:grid-cols-2 gap-8">
            {/* Standard Checkboxes */}
            <div>
              <Heading level={4} className="text-neutral-900">Standard Checkboxes</Heading>
              <div className="space-y-4 mb-4">
                <label className="checkbox-label">
                  <input type="checkbox" />
                  <span>Standard checkbox option</span>
                </label>
                
                <label className="checkbox-label">
                  <input type="checkbox" defaultChecked />
                  <span>Checked checkbox option</span>
                </label>
                
                <label className="checkbox-label">
                  <input type="checkbox" disabled />
                  <span>Disabled checkbox option</span>
                </label>
                
                <label className="checkbox-label">
                  <input type="checkbox" disabled defaultChecked />
                  <span>Disabled checked option</span>
                </label>
              </div>
              
              <div className="bg-neutral-50 p-3 hairline">
                <Text size="sm" className="font-mono mb-2">STANDARD FEATURES:</Text>
                <ul className="list-disc list-inside space-y-1 text-xs text-neutral-900">
                  <li>20px × 20px square dimensions</li>
                  <li>2px black border (#0D0D0D)</li>
                  <li>Square corners (no border-radius)</li>
                  <li>White checkmark on black background</li>
                  <li>Hover and focus states</li>
                </ul>
              </div>
            </div>

            {/* Checkbox Variants */}
            <div>
              <Heading level={4} className="text-neutral-900">Checkbox Variants</Heading>
              <div className="space-y-4 mb-4">
                <label className="checkbox-label">
                  <input type="checkbox" className="checkbox-compact" />
                  <span>Compact checkbox (16px)</span>
                </label>
                
                <label className="checkbox-label">
                  <input type="checkbox" />
                  <span>Standard checkbox (20px)</span>
                </label>
                
                <label className="checkbox-label">
                  <input type="checkbox" className="checkbox-large" />
                  <span>Large checkbox (24px)</span>
                </label>
              </div>
              
              <div className="bg-neutral-50 p-3 hairline">
                <Text size="sm" className="font-mono mb-2">VARIANT SIZES:</Text>
                <ul className="list-disc list-inside space-y-1 text-xs text-neutral-900">
                  <li>Compact: 16px × 16px (1px border)</li>
                  <li>Standard: 20px × 20px (2px border)</li>
                  <li>Large: 24px × 24px (2px border)</li>
                  <li>Mobile: 18px × 18px (responsive)</li>
                </ul>
              </div>
            </div>
          </div>
        </div>

        {/* Checkbox States */}
        <div className="bg-neutral-0 hairline minimal-padding vspace mb-8">
          <Heading level={3} className="text-neutral-900">Checkbox States</Heading>
          
          <div className="grid grid-cols-1 md:grid-cols-3 gap-6">
            <div className="bg-neutral-50 p-4 hairline">
              <Heading level={4} className="text-neutral-900 mb-3">Default State</Heading>
              <label className="checkbox-label">
                <input type="checkbox" />
                <span>Unchecked checkbox</span>
              </label>
              <Text size="xs" className="text-neutral-900 opacity-60 mt-2 font-mono">
                White background, red border
              </Text>
            </div>
            
            <div className="bg-neutral-50 p-4 hairline">
              <Heading level={4} className="text-neutral-900 mb-3">Checked State</Heading>
              <label className="checkbox-label">
                <input type="checkbox" defaultChecked />
                <span>Checked checkbox</span>
              </label>
              <Text size="xs" className="text-neutral-900 opacity-60 mt-2 font-mono">
                Red background, white checkmark
              </Text>
            </div>
            
            <div className="bg-neutral-50 p-4 hairline">
              <Heading level={4} className="text-neutral-900 mb-3">Hover State</Heading>
              <label className="checkbox-label">
                <input type="checkbox" />
                <span>Hover to see effect</span>
              </label>
              <Text size="xs" className="text-neutral-900 opacity-60 mt-2 font-mono">
                Light red background tint
              </Text>
            </div>
          </div>
        </div>

        {/* Checkbox Implementation */}
        <div className="bg-neutral-0 hairline minimal-padding vspace mb-8">
          <Heading level={3} className="text-neutral-900">Implementation</Heading>
          
          <div className="bg-neutral-50 p-4 hairline mb-4">
            <Text size="sm" className="font-mono mb-2">HTML STRUCTURE:</Text>
            <div className="text-xs text-neutral-900 font-mono">
              &lt;label className="checkbox-label"&gt;
              <br />
              &nbsp;&nbsp;&lt;input type="checkbox" /&gt;
              <br />
              &nbsp;&nbsp;&lt;span&gt;Checkbox label text&lt;/span&gt;
              <br />
              &lt;/label&gt;
            </div>
          </div>
          
          <div className="bg-neutral-50 p-4 hairline">
            <Text size="sm" className="font-mono mb-2">CSS FEATURES:</Text>
            <ul className="list-disc list-inside space-y-1 text-xs text-neutral-900">
              <li>Global styles in checkboxes.css</li>
              <li>Square corners with border-radius: 0</li>
              <li>Consistent black accent color (#0D0D0D)</li>
              <li>Responsive sizing for mobile devices</li>
              <li>Accessible focus and hover states</li>
              <li>Disabled state styling</li>
            </ul>
          </div>
        </div>

        {/* Advanced Pricing Component */}
        <div className="bg-neutral-0 hairline minimal-padding vspace mb-2">
          <Heading level={2} className="text-neutral-900" showInterLine style={{ color: colorTokens.oxfordBlue }}>Advanced Pricing Component</Heading>
          
          <Text className="text-neutral-900">
            Interactive pricing cards with dynamic text alignment, feature comparison, and smooth hover animations. 
            All features are listed across all cards with included/excluded states and dividing lines.
          </Text>
        </div>

        <div className="bg-neutral-0 hairline minimal-padding vspace mb-8">
          <div className="space-y-8">
            {/* Advanced Pricing Demo */}
            <div>
              <Heading level={4} className="text-neutral-900 mb-4">Interactive Pricing Cards</Heading>
              <Text size="sm" className="text-neutral-900 mb-6">
                Hover over different cards to see the dynamic text alignment and feature highlighting in action.
              </Text>
              
              <AdvancedPricingTiers 
                currentPlan="free"
                onSelectPlan={(plan) => console.log('Selected plan:', plan)}
                loading={false}
              />
            </div>

            {/* Feature Breakdown */}
            <div className="bg-neutral-50 p-4 hairline">
              <Text size="sm" className="font-mono mb-2">ADVANCED PRICING FEATURES:</Text>
              <ul className="list-disc list-inside space-y-1 text-xs text-neutral-900">
                <li>All features listed across all cards with included/excluded states</li>
                <li>Dividing lines between each feature for visual separation</li>
                <li>Dynamic text alignment based on hover position (left/center/right)</li>
                <li>Smooth 300ms transitions for all alignment changes</li>
                <li>Grayed out features for plans that don't include them</li>
                <li>Hover elevation with shadow and z-index changes</li>
                <li>Consistent hairline borders and spacing</li>
                <li>Current plan highlighting and button states</li>
              </ul>
            </div>

            {/* Implementation Details */}
            <div className="bg-neutral-50 p-4 hairline">
              <Text size="sm" className="font-mono mb-2">TEXT ALIGNMENT LOGIC:</Text>
              <div className="text-xs text-neutral-900 font-mono space-y-1">
                <div>• Left of hovered card: text-left</div>
                <div>• Hovered card: text-center</div>
                <div>• Right of hovered card: text-right</div>
                <div>• No hover: all cards text-center</div>
              </div>
            </div>
          </div>
        </div>

        {/* List/Feed Interfaces */}
        <div className="bg-neutral-0 hairline minimal-padding vspace mb-2">
          <Heading level={2} className="text-neutral-900" showInterLine style={{ color: colorTokens.oxfordBlue }}>List/Feed Interfaces</Heading>
          
          <Text className="text-neutral-900">
            Clean, scannable list interfaces for notifications, posts, and activity feeds. Emphasizes content hierarchy, proper spacing, and clear interaction patterns.
          </Text>
        </div>

        <div className="bg-neutral-0 hairline minimal-padding vspace mb-8">
          <div className="grid grid-cols-1 lg:grid-cols-2 gap-8">
            {/* Notification Cards */}
            <div>
              <Heading level={4} className="text-neutral-900">Notification Cards</Heading>
              <Text size="sm" className="text-neutral-900 mb-4">
                Compact notification cards with proper spacing, content preview, and square action buttons.
              </Text>
              
              <div className="space-y-2">
                {/* Unread Notification */}
                <div className="bg-neutral-50 p-3 hairline">
                  <div className="flex items-start space-x-3">
                    {/* User Profile Picture */}
                    <div className="w-10 h-10 bg-neutral-0 hairline flex items-center justify-center text-sm font-mono flex-shrink-0">
                      JD
                    </div>
                    
                    <div className="flex-1 min-w-0">
                      <div className="flex items-start justify-between">
                        <div className="flex-1">
                          {/* User Name and Notification Info */}
                          <div className="flex items-center space-x-2 mb-1">
                            <Text className="text-sm font-medium text-neutral-900">
                              John Doe
                            </Text>
                            <div className="flex items-center space-x-2">
                              <div className="text-lg">⟷</div>
                              <Text className="text-sm font-medium text-neutral-900">
                                Merge Proposal
                              </Text>
                            </div>
                            <div className="w-2 h-2 bg-blue-600 rounded-full flex-shrink-0"></div>
                          </div>
                          
                          <div className="h-px bg-neutral-900 opacity-20 mb-2"></div>
                          
                          <Text className="text-xs text-neutral-900 opacity-80 mb-2 leading-relaxed">
                            John wants to merge changes to your "Pasta Carbonara" recipe...
                          </Text>
                          
                          <div className="flex items-center space-x-3 text-xs text-neutral-900 opacity-60">
                            <span>2h ago</span>
                          </div>
                        </div>
                        
                        <div className="flex items-center space-x-1 ml-3">
                          <button className="w-6 h-6 bg-neutral-900 text-neutral-0 flex items-center justify-center text-xs font-mono hover:opacity-60 transition-opacity" title="View">→</button>
                          <button className="w-6 h-6 bg-neutral-50 text-neutral-900 hairline hover:bg-neutral-900 hover:text-neutral-0 transition-colors text-xs font-mono" title="Delete">✕</button>
                        </div>
                      </div>
                    </div>
                  </div>
                </div>

                {/* Read Notification */}
                <div className="bg-neutral-0 p-3 hairline">
                  <div className="flex items-start space-x-3">
                    {/* User Profile Picture */}
                    <div className="w-10 h-10 bg-neutral-0 hairline flex items-center justify-center text-sm font-mono flex-shrink-0">
                      SW
                    </div>
                    
                    <div className="flex-1 min-w-0">
                      <div className="flex items-start justify-between">
                        <div className="flex-1">
                          {/* User Name and Notification Info */}
                          <div className="flex items-center space-x-2 mb-1">
                            <Text className="text-sm font-medium text-neutral-900">
                              Sarah Wilson
                            </Text>
                            <div className="flex items-center space-x-2">
                              <div className="text-lg">✓</div>
                              <Text className="text-sm font-medium text-neutral-900">
                                Merge Approved
                              </Text>
                            </div>
                          </div>
                          
                          <div className="h-px bg-neutral-900 opacity-20 mb-2"></div>
                          
                          <Text className="text-xs text-neutral-900 opacity-80 mb-2 leading-relaxed">
                            Your merge request for "Chocolate Chip Cookies" has been approved...
                          </Text>
                          
                          <div className="flex items-center space-x-3 text-xs text-neutral-900 opacity-60">
                            <span>1d ago</span>
                          </div>
                        </div>
                        
                        <div className="flex items-center space-x-1 ml-3">
                          <button className="w-6 h-6 bg-neutral-900 text-neutral-0 flex items-center justify-center text-xs font-mono hover:opacity-60 transition-opacity" title="View">→</button>
                          <button className="w-6 h-6 bg-neutral-50 text-neutral-900 hairline hover:bg-neutral-900 hover:text-neutral-0 transition-colors text-xs font-mono" title="Delete">✕</button>
                        </div>
                      </div>
                    </div>
                  </div>
                </div>
              </div>
              
              <div className="bg-neutral-50 p-3 hairline mt-4">
                <Text size="sm" className="font-mono mb-2">NOTIFICATION CARD FEATURES:</Text>
                <ul className="list-disc list-inside space-y-1 text-xs text-neutral-900">
                  <li>User profile picture with initials fallback</li>
                  <li>User name displayed first for context</li>
                  <li>Notification icon and title grouped together</li>
                  <li>Compact padding (p-3) for better density</li>
                  <li>Content preview with ellipsis truncation</li>
                  <li>Divider line between title and content</li>
                  <li>Square action buttons (6x6) for consistency</li>
                  <li>Unread indicator dot for visual hierarchy</li>
                  <li>Direct modal navigation for specific types</li>
                  <li>Friend requests and merge proposals open immediately</li>
                </ul>
              </div>
            </div>

            {/* Post Cards */}
            <div>
              <Heading level={4} className="text-neutral-900">Post Cards</Heading>
              <Text size="sm" className="text-neutral-900 mb-4">
                Clean post cards with proper image handling and interaction patterns.
              </Text>
              
              <div className="space-y-3">
                <div className="bg-neutral-50 p-3 hairline">
                  <div className="flex items-center justify-between mb-3">
                    <div className="flex items-center gap-3">
                      <div className="w-10 h-10 bg-neutral-0 text-neutral-900 hairline flex items-center justify-center font-mono text-sm font-medium">JD</div>
                      <div>
                        <Text className="font-medium text-neutral-900">John Doe</Text>
                        <Text className="text-sm text-neutral-900 opacity-60">cooked Pasta Carbonara</Text>
                      </div>
                    </div>
                    <div className="flex items-center gap-3">
                      <button className="p-2 text-neutral-900 hover:bg-neutral-0 hairline transition-colors">
                        <svg className="w-4 h-4" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                          <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M8.684 13.342C8.886 12.938 9 12.482 9 12c0-.482-.114-.938-.316-1.342m0 2.684a3 3 0 110-2.684m0 2.684l6.632 3.316m-6.632-6l6.632-3.316m0 0a3 3 0 105.367-2.684 3 3 0 00-5.367 2.684zm0 9.316a3 3 0 105.367 2.684 3 3 0 00-5.367-2.684z" />
                        </svg>
                      </button>
                      <Text className="text-sm font-mono text-neutral-900 opacity-60">2h ago</Text>
                    </div>
                  </div>
                  
                  <div className="h-px bg-neutral-900 opacity-20 mb-3"></div>
                  
                  <Text className="text-sm leading-relaxed text-neutral-900 mb-3">
                    Just made the most amazing pasta carbonara! The recipe from the community was perfect.
                  </Text>
                  
                  <div className="aspect-video bg-neutral-0 hairline mb-3 flex items-center justify-center">
                    <Text size="sm" className="font-mono text-neutral-900 opacity-60">Post Image</Text>
                  </div>
                  
                  <div className="flex items-center justify-between text-xs font-mono text-neutral-900 opacity-60">
                    <div className="flex items-center gap-4">
                      <span>Session: 2h ago</span>
                      <span>Duration: 45m</span>
                    </div>
                    <button className="px-3 py-1 font-mono text-xs uppercase tracking-wider text-white hover:opacity-60 transition-opacity" style={{ backgroundColor: '#1b263b' }}>
                      Report
                    </button>
                  </div>
                </div>
              </div>
              
              <div className="bg-neutral-50 p-3 hairline mt-4">
                <Text size="sm" className="font-mono mb-2">POST CARD FEATURES:</Text>
                <ul className="list-disc list-inside space-y-1 text-xs text-neutral-900">
                  <li>User avatar with initials fallback</li>
                  <li>Clear author and timestamp information</li>
                  <li>Aspect ratio images for consistency</li>
                  <li>Simple interaction buttons</li>
                  <li>Proper content hierarchy</li>
                </ul>
              </div>
            </div>
          </div>
        </div>

        {/* Modal Detail Patterns */}
        <div className="bg-neutral-0 hairline minimal-padding vspace mb-2">
          <Heading level={2} className="text-neutral-900" showInterLine style={{ color: colorTokens.oxfordBlue }}>Modal Detail Patterns</Heading>
          
          <Text className="text-neutral-900">
            Detailed modal interfaces for expanded content views. All actions are contained within the modal for better user experience.
          </Text>
        </div>

        <div className="bg-neutral-0 hairline minimal-padding vspace mb-8">
          <div className="grid grid-cols-1 lg:grid-cols-2 gap-8">
            {/* Notification Detail Modal */}
            <div>
              <Heading level={4} className="text-neutral-900">Notification Detail Modal</Heading>
              <Text size="sm" className="text-neutral-900 mb-4">
                Full notification details with all available actions as buttons within the modal.
              </Text>
              
              <div className="bg-neutral-50 p-4 hairline">
                <div className="flex items-center justify-between mb-4">
                  <Heading level={4} className="text-neutral-900">Merge Proposal</Heading>
                  <button className="text-neutral-900 hover:opacity-60 transition-opacity duration-120 font-mono text-lg border-none bg-transparent p-0 m-0 modal-close">
                    ✕
                  </button>
                </div>
                
                <div className="space-y-4">
                  <div className="flex items-start space-x-3">
                    <div className="text-2xl">⟷</div>
                    <div className="flex-1">
                      <Text className="text-sm font-medium text-neutral-900 mb-2">John Doe wants to merge changes</Text>
                      <Text className="text-sm text-neutral-900 opacity-80 mb-3">
                        John has proposed changes to your "Pasta Carbonara" recipe. The changes include updated cooking times and ingredient measurements based on community feedback.
                      </Text>
                      <div className="flex items-center space-x-4 text-xs text-neutral-900 opacity-60 mb-4">
                        <span>2 hours ago</span>
                        <span>from John Doe</span>
                      </div>
                    </div>
                  </div>
                  
                  <div className="h-px bg-neutral-900 opacity-20"></div>
                  
                  <div className="flex space-x-3">
                    <button className="px-4 py-2 bg-neutral-900 text-neutral-0 font-mono text-sm uppercase tracking-wider hover:opacity-60 transition-opacity">
                      VIEW CHANGES
                    </button>
                    <button className="px-4 py-2 bg-neutral-50 text-neutral-900 hairline hover:bg-neutral-900 hover:text-neutral-0 transition-colors font-mono text-sm uppercase tracking-wider">
                      APPROVE
                    </button>
                    <button className="px-4 py-2 bg-neutral-50 text-neutral-900 hairline hover:bg-neutral-900 hover:text-neutral-0 transition-colors font-mono text-sm uppercase tracking-wider">
                      REJECT
                    </button>
                  </div>
                </div>
              </div>
              
              <div className="bg-neutral-50 p-3 hairline mt-4">
                <Text size="sm" className="font-mono mb-2">MODAL FEATURES:</Text>
                <ul className="list-disc list-inside space-y-1 text-xs text-neutral-900">
                  <li>All actions contained within modal</li>
                  <li>Clear close button (X only)</li>
                  <li>Full content display with proper spacing</li>
                  <li>Action buttons grouped at bottom</li>
                  <li>Consistent button styling</li>
                </ul>
              </div>
            </div>

            {/* User Profile Access */}
            <div>
              <Heading level={4} className="text-neutral-900">User Profile Access</Heading>
              <Text size="sm" className="text-neutral-900 mb-4">
                Clickable user elements that open profile modals or navigate to user pages.
              </Text>
              
              <div className="bg-neutral-50 p-4 hairline">
                <div className="flex items-center space-x-3 mb-4">
                  <button className="w-10 h-10 bg-neutral-0 hairline flex items-center justify-center text-sm font-mono hover:opacity-60 transition-opacity">
                    JD
                  </button>
                  <div>
                    <button className="text-sm font-medium text-neutral-900 hover:opacity-60 transition-opacity">
                      John Doe
                    </button>
                    <Text className="text-xs text-neutral-900 opacity-60">2 hours ago</Text>
                  </div>
                </div>
                
                <Text className="text-sm text-neutral-900 mb-4">
                  Click on the user avatar or name to view their profile and cooking history.
                </Text>
                
                <div className="bg-neutral-0 p-3 hairline">
                  <Text size="sm" className="font-mono mb-2">USER INTERACTION FEATURES:</Text>
                  <ul className="list-disc list-inside space-y-1 text-xs text-neutral-900">
                    <li>Clickable avatar and username</li>
                    <li>Hover states for interactive elements</li>
                    <li>Profile modal or navigation</li>
                    <li>Consistent user representation</li>
                  </ul>
                </div>
              </div>
            </div>
          </div>
        </div>

        {/* Chef Character System */}
        <div className="bg-neutral-0 hairline minimal-padding vspace mb-8">
          <Heading level={2} className="text-neutral-900" showInterLine>Chef Character System</Heading>
          
          <Text className="text-neutral-900 mb-4">
            Complete collection of chef character states used throughout the platform. Each state represents different cooking activities and user interactions.
          </Text>
          
          {/* Live Animation Demo */}
          <div className="bg-neutral-50 p-4 hairline mb-6">
            <Text size="sm" className="font-mono mb-4">LIVE ANIMATION DEMO:</Text>
            <PixelArtLoader 
              message="STYLE GUIDE DEMO"
              subMessage="Chef animation in action..."
            />
          </div>
          
          {/* Chef States Gallery */}
          <div className="bg-neutral-50 p-4 hairline mb-6">
            <Text size="sm" className="font-mono mb-4">CHEF CHARACTER STATES:</Text>
            <div className="grid grid-cols-2 md:grid-cols-3 lg:grid-cols-4 gap-4">
              <div className="text-center">
                <div className="w-16 h-16 bg-neutral-0 hairline mb-2 flex items-center justify-center">
                  <img 
                    src="/images/Chef.png" 
                    alt="Default Chef" 
                    className="w-12 h-12 object-contain"
                  />
                </div>
                <Text size="xs" className="font-mono text-neutral-900">Default Chef</Text>
              </div>
              
              <div className="text-center">
                <div className="w-16 h-16 bg-neutral-0 hairline mb-2 flex items-center justify-center">
                  <img 
                    src="/images/Chef-thinking.png" 
                    alt="Chef Thinking" 
                    className="w-12 h-12 object-contain"
                  />
                </div>
                <Text size="xs" className="font-mono text-neutral-900">Thinking</Text>
              </div>
              
              <div className="text-center">
                <div className="w-16 h-16 bg-neutral-0 hairline mb-2 flex items-center justify-center">
                  <img 
                    src="/images/Chef-talking.png" 
                    alt="Chef Talking" 
                    className="w-12 h-12 object-contain"
                  />
                </div>
                <Text size="xs" className="font-mono text-neutral-900">Talking</Text>
              </div>
              
              <div className="text-center">
                <div className="w-16 h-16 bg-neutral-0 hairline mb-2 flex items-center justify-center">
                  <img 
                    src="/images/Chef-listening.png" 
                    alt="Chef Listening" 
                    className="w-12 h-12 object-contain"
                  />
                </div>
                <Text size="xs" className="font-mono text-neutral-900">Listening</Text>
              </div>
              
              <div className="text-center">
                <div className="w-16 h-16 bg-neutral-0 hairline mb-2 flex items-center justify-center">
                  <img 
                    src="/images/Chef-sad.png" 
                    alt="Chef Sad" 
                    className="w-12 h-12 object-contain"
                  />
                </div>
                <Text size="xs" className="font-mono text-neutral-900">Sad</Text>
              </div>
              
              <div className="text-center">
                <div className="w-16 h-16 bg-neutral-0 hairline mb-2 flex items-center justify-center">
                  <img 
                    src="/images/Chef-indifferent.png" 
                    alt="Chef Indifferent" 
                    className="w-12 h-12 object-contain"
                  />
                </div>
                <Text size="xs" className="font-mono text-neutral-900">Indifferent</Text>
              </div>
              
              <div className="text-center">
                <div className="w-16 h-16 bg-neutral-0 hairline mb-2 flex items-center justify-center">
                  <img 
                    src="/images/chef_pre_fire.png" 
                    alt="Chef Pre-Fire" 
                    className="w-12 h-12 object-contain"
                  />
                </div>
                <Text size="xs" className="font-mono text-neutral-900">Pre-Fire</Text>
              </div>
              
              <div className="text-center">
                <div className="w-16 h-16 bg-neutral-0 hairline mb-2 flex items-center justify-center">
                  <img 
                    src="/images/chef_fire.png" 
                    alt="Chef Fire" 
                    className="w-12 h-12 object-contain"
                  />
                </div>
                <Text size="xs" className="font-mono text-neutral-900">Fire</Text>
              </div>
            </div>
          </div>
          
          <div className="bg-neutral-50 p-4 hairline">
            <Text size="sm" className="font-mono mb-2">CHEF CHARACTER FEATURES:</Text>
            <ul className="list-disc list-inside space-y-1 text-xs text-neutral-900 mb-4">
              <li>8 distinct character states for different cooking contexts</li>
              <li>Consistent 64x64 pixel art styling across all states</li>
              <li>Pre-fire and fire states with snapping animation for loading</li>
              <li>Emotional states for user interaction feedback</li>
              <li>Voice interaction states (talking, listening, thinking)</li>
              <li>Used across loading states, voice assistant, and UI feedback</li>
            </ul>
            
            <Text size="sm" className="font-mono mb-2">LOADING ANIMATION USAGE:</Text>
            <div className="text-xs text-neutral-900 font-mono bg-neutral-0 p-2 hairline">
              &lt;PixelArtLoader <br />
              &nbsp;&nbsp;message="LOADING RECIPES" <br />
              &nbsp;&nbsp;subMessage="Fetching your culinary collection..." <br />
              /&gt;
            </div>
          </div>
        </div>

        {/* Walkthrough Testing */}
        <div className="bg-neutral-0 hairline minimal-padding vspace mb-2">
          <Heading level={2} className="text-neutral-900" showInterLine style={{ color: colorTokens.oxfordBlue }}>Walkthrough Testing</Heading>
          
          <Text className="text-neutral-900">
            Test the user education walkthrough system with Chef as your guide. This system helps new users learn how to create their first recipe step by step.
          </Text>
        </div>

        <div className="bg-neutral-0 hairline minimal-padding vspace mb-8">
          <div className="grid grid-cols-1 lg:grid-cols-2 gap-8">
            {/* Walkthrough Demo */}
            <div>
              <Heading level={4} className="text-neutral-900">Recipe Creation Walkthrough</Heading>
              <Text size="sm" className="text-neutral-900 mb-4">
                Experience the complete walkthrough flow that new users see when they create their first recipe. Chef will guide you through each step with interactive highlights and helpful tips.
              </Text>
              
              <div className="space-y-3 mb-4">
                <button 
                  onClick={() => {
                    setCurrentPage('recipes');
                    setShowWalkthrough(true);
                  }}
                  className="px-4 py-2 bg-neutral-900 text-neutral-0 font-mono text-sm uppercase tracking-wider hover:opacity-60 transition-opacity"
                >
                  Start Recipe Walkthrough (Mock)
                </button>
                
                <button 
                  onClick={() => {
                    console.log('🧪 Testing walkthrough context directly - not available in StyleGuide');
                    alert('Walkthrough context testing is only available on pages within the WalkthroughProvider. Use the mock walkthrough above instead.');
                  }}
                  className="px-4 py-2 bg-neutral-900 text-neutral-0 font-mono text-sm uppercase tracking-wider hover:opacity-60 transition-opacity"
                >
                  Start Walkthrough (Context)
                </button>
                
                <button 
                  onClick={() => {
                    console.log('🧪 Resetting walkthrough context');
                    setWalkthroughStep(0);
                    setCurrentPage('recipes');
                    setShowWalkthrough(false);
                  }}
                  className="px-4 py-2 bg-neutral-50 text-neutral-900 hairline hover:bg-neutral-900 hover:text-neutral-0 transition-colors font-mono text-sm uppercase tracking-wider"
                >
                  Reset Walkthrough
                </button>
              </div>
              
              <div className="bg-neutral-50 p-3 hairline">
                <Text size="sm" className="font-mono mb-2">WALKTHROUGH FEATURES:</Text>
                <ul className="list-disc list-inside space-y-1 text-xs text-neutral-900">
                  <li>Chef character with animated speech bubbles</li>
                  <li>Interactive element highlighting</li>
                  <li>Step-by-step guidance with typing animation</li>
                  <li>Floating chef tab for continuous assistance</li>
                  <li>Screen overlay to focus attention</li>
                  <li>Example recipe URL for Smart Transfer</li>
                </ul>
              </div>
            </div>

            {/* Walkthrough Steps */}
            <div>
              <Heading level={4} className="text-neutral-900">Walkthrough Steps</Heading>
              <Text size="sm" className="text-neutral-900 mb-4">
                The walkthrough guides users through these key steps in recipe creation:
              </Text>
              
              <div className="space-y-2">
                <div className="bg-neutral-50 p-3 hairline">
                  <Text size="sm" className="font-mono text-neutral-900 mb-1">STEP 1: Welcome</Text>
                  <Text size="xs" className="text-neutral-900 opacity-80">Chef introduces himself and explains the walkthrough</Text>
                </div>
                
                <div className="bg-neutral-50 p-3 hairline">
                  <Text size="sm" className="font-mono text-neutral-900 mb-1">STEP 2: Create Button</Text>
                  <Text size="xs" className="text-neutral-900 opacity-80">Highlights the Create button to start recipe creation</Text>
                </div>
                
                <div className="bg-neutral-50 p-3 hairline">
                  <Text size="sm" className="font-mono text-neutral-900 mb-1">STEP 3: Smart Transfer</Text>
                  <Text size="xs" className="text-neutral-900 opacity-80">Explains how to use Smart Transfer with example URL</Text>
                </div>
                
                <div className="bg-neutral-50 p-3 hairline">
                  <Text size="sm" className="font-mono text-neutral-900 mb-1">STEP 4: Transfer Button</Text>
                  <Text size="xs" className="text-neutral-900 opacity-80">Guides user to click Transfer after pasting URL</Text>
                </div>
                
                <div className="bg-neutral-50 p-3 hairline">
                  <Text size="sm" className="font-mono text-neutral-900 mb-1">STEP 5: Create Recipe</Text>
                  <Text size="xs" className="text-neutral-900 opacity-80">Final step to complete recipe creation</Text>
                </div>
                
                <div className="bg-neutral-50 p-3 hairline">
                  <Text size="sm" className="font-mono text-neutral-900 mb-1">STEP 6: Completion</Text>
                  <Text size="xs" className="text-neutral-900 opacity-80">Celebration message and walkthrough completion</Text>
                </div>
              </div>
            </div>
          </div>
        </div>

        {/* Tab System */}
        <div className="bg-neutral-0 hairline minimal-padding vspace mb-2">
          <Heading level={2} className="text-neutral-900" showInterLine style={{ color: colorTokens.oxfordBlue }}>Tab System</Heading>
          
          <Text className="text-neutral-900">
            Reusable Tab component for creating toggle buttons throughout the app. Tabs can be positioned at any edge and support sliver states for minimal UI footprint.
          </Text>
        </div>

        <div className="bg-neutral-0 hairline minimal-padding vspace mb-8">
          <div className="grid grid-cols-1 lg:grid-cols-2 gap-8">
            {/* Tab Component Demo */}
            <div>
              <Heading level={4} className="text-neutral-900">Reusable Tab Component</Heading>
              <Text size="sm" className="text-neutral-900 mb-4">
                The Tab component is fully customizable with configurable position, image, visibility state, and click behavior.
              </Text>
              
              <div className="space-y-4 mb-4">
                <div className="bg-neutral-50 p-4 hairline">
                  <Text size="sm" className="font-mono mb-2">TAB PROPS:</Text>
                  <ul className="list-disc list-inside space-y-1 text-xs text-neutral-900">
                    <li><code className="font-mono">position</code>: 'top' | 'bottom' | 'left' | 'right'</li>
                    <li><code className="font-mono">image</code>: string (image src path)</li>
                    <li><code className="font-mono">alt</code>: string (alt text)</li>
                    <li><code className="font-mono">onClick</code>: function (click handler)</li>
                    <li><code className="font-mono">isVisible</code>: boolean (full visibility vs sliver)</li>
                    <li><code className="font-mono">sliverSize</code>: number (pixels visible when hidden)</li>
                  </ul>
                </div>
              </div>
              
              <div className="bg-neutral-50 p-3 hairline">
                <Text size="sm" className="font-mono mb-2">DEMO CONTROLS:</Text>
                <button
                  onClick={() => setDemoTabVisible(!demoTabVisible)}
                  className="px-4 py-2 bg-neutral-900 text-neutral-0 font-mono text-sm uppercase tracking-wider hover:opacity-60 transition-opacity mb-2"
                >
                  {demoTabVisible ? 'Hide Tab' : 'Show Tab'}
                </button>
                <Text size="xs" className="text-neutral-900 opacity-60">
                  Toggle tab visibility to see sliver state
                </Text>
              </div>
            </div>

            {/* Header Toggle Tab Demo */}
            <div>
              <Heading level={4} className="text-neutral-900">Header Toggle Tab</Heading>
              <Text size="sm" className="text-neutral-900 mb-4">
                The HeaderToggleTab is a specialized implementation that controls header and navigation visibility. By default, the header shows for 2 seconds then auto-closes.
              </Text>
              
              <div className="bg-neutral-50 p-4 hairline mb-4">
                <div className="flex items-center justify-between mb-3">
                  <Text size="sm" className="font-mono text-neutral-900">Header State:</Text>
                  <span className={`px-3 py-1 font-mono text-xs ${isHeaderVisible ? 'bg-neutral-900 text-neutral-0' : 'bg-neutral-0 text-neutral-900 hairline'}`}>
                    {isHeaderVisible ? 'VISIBLE' : 'HIDDEN'}
                  </span>
                </div>
                <button
                  onClick={toggleHeader}
                  className="px-4 py-2 bg-neutral-900 text-neutral-0 font-mono text-sm uppercase tracking-wider hover:opacity-60 transition-opacity w-full"
                >
                  Toggle Header
                </button>
              </div>
              
              <div className="bg-neutral-50 p-3 hairline">
                <Text size="sm" className="font-mono mb-2">HEADER TOGGLE FEATURES:</Text>
                <ul className="list-disc list-inside space-y-1 text-xs text-neutral-900">
                  <li>Auto-closes after 2 seconds on page load</li>
                  <li>Tab shows sliver when header is visible</li>
                  <li>Tab fully visible when header is hidden</li>
                  <li>Smooth slide animations (300ms)</li>
                  <li>Uses 4RKED logo (/4.svg) as tab image</li>
                </ul>
              </div>
            </div>
          </div>

          {/* Tab Position Examples */}
          <div className="mt-8">
            <Heading level={3} className="text-neutral-900 mb-4">Tab Position Examples</Heading>
            <div className="grid grid-cols-1 md:grid-cols-2 gap-6">
              <div className="bg-neutral-50 p-4 hairline">
                <Text size="sm" className="font-mono mb-2">TOP POSITION:</Text>
                <Text size="xs" className="text-neutral-900 opacity-80 mb-2">
                  Fixed at top center. Used for header toggle.
                </Text>
                <div className="text-xs text-neutral-900 font-mono bg-neutral-0 p-2 hairline">
                  position="top"
                </div>
              </div>
              
              <div className="bg-neutral-50 p-4 hairline">
                <Text size="sm" className="font-mono mb-2">BOTTOM POSITION:</Text>
                <Text size="xs" className="text-neutral-900 opacity-80 mb-2">
                  Fixed at bottom center. Useful for action buttons.
                </Text>
                <div className="text-xs text-neutral-900 font-mono bg-neutral-0 p-2 hairline">
                  position="bottom"
                </div>
              </div>
              
              <div className="bg-neutral-50 p-4 hairline">
                <Text size="sm" className="font-mono mb-2">LEFT POSITION:</Text>
                <Text size="xs" className="text-neutral-900 opacity-80 mb-2">
                  Fixed at left edge, vertically centered.
                </Text>
                <div className="text-xs text-neutral-900 font-mono bg-neutral-0 p-2 hairline">
                  position="left"
                </div>
              </div>
              
              <div className="bg-neutral-50 p-4 hairline">
                <Text size="sm" className="font-mono mb-2">RIGHT POSITION:</Text>
                <Text size="xs" className="text-neutral-900 opacity-80 mb-2">
                  Fixed at right edge, vertically centered.
                </Text>
                <div className="text-xs text-neutral-900 font-mono bg-neutral-0 p-2 hairline">
                  position="right"
                </div>
              </div>
            </div>
          </div>

          {/* Sliver State Explanation */}
          <div className="mt-8 bg-neutral-50 p-4 hairline">
            <Text size="sm" className="font-mono mb-2">SLIVER STATE BEHAVIOR:</Text>
            <ul className="list-disc list-inside space-y-1 text-xs text-neutral-900 mb-4">
              <li>When <code className="font-mono">isVisible={'{false}'}</code>: Only shows sliver (default 10px)</li>
              <li>When <code className="font-mono">isVisible={'{true}'}</code>: Fully visible (48px height/width)</li>
              <li>Smooth CSS transitions for state changes (200ms ease-out)</li>
              <li>Content is clipped when in sliver state to prevent overflow</li>
              <li>Hover states maintain opacity-60 transition</li>
            </ul>
            
            <div className="bg-neutral-0 p-3 hairline">
              <Text size="sm" className="font-mono mb-2">USAGE EXAMPLE:</Text>
              <div className="text-xs text-neutral-900 font-mono">
                &lt;Tab<br />
                &nbsp;&nbsp;position="top"<br />
                &nbsp;&nbsp;image="/4.svg"<br />
                &nbsp;&nbsp;onClick={'{handleToggle}'}<br />
                &nbsp;&nbsp;isVisible={'{!isHeaderVisible}'}<br />
                &nbsp;&nbsp;sliverSize={'{10}'}<br />
                /&gt;
              </div>
            </div>
          </div>
        </div>

        {/* Lineage Walkthrough Testing */}
        <div className="bg-neutral-0 hairline minimal-padding vspace mb-2">
          <Heading level={2} className="text-neutral-900" showInterLine style={{ color: colorTokens.oxfordBlue }}>Lineage Walkthrough Testing</Heading>
          
          <Text className="text-neutral-900">
            Test the lineage walkthrough system. Navigate to a recipe detail page to test the walkthrough functionality.
          </Text>
        </div>

        <div className="bg-neutral-0 hairline minimal-padding vspace mb-8">
          <div className="grid grid-cols-1 lg:grid-cols-2 gap-8">
            {/* Test Navigation */}
            <div>
              <Heading level={4} className="text-neutral-900">Test Recipe Detail Page</Heading>
              <Text size="sm" className="text-neutral-900 mb-4">
                Click the button below to navigate to a recipe detail page where you can test the lineage walkthrough system.
              </Text>
              
              <div className="space-y-3 mb-4">
                <button 
                  onClick={() => navigate('/recipes/1')}
                  className="px-4 py-2 bg-neutral-900 text-neutral-0 font-mono text-sm uppercase tracking-wider hover:opacity-60 transition-opacity"
                >
                  TEST RECIPE ID: 1
                </button>
                
                <button 
                  onClick={() => navigate('/recipes/56')}
                  className="px-4 py-2 bg-neutral-900 text-neutral-0 font-mono text-sm uppercase tracking-wider hover:opacity-60 transition-opacity"
                >
                  TEST RECIPE ID: 56
                </button>
                
                <button 
                  onClick={() => navigate('/recipes/57')}
                  className="px-4 py-2 bg-neutral-900 text-neutral-0 font-mono text-sm uppercase tracking-wider hover:opacity-60 transition-opacity"
                >
                  TEST RECIPE ID: 57
                </button>
              </div>
              
              <div className="bg-neutral-50 p-3 hairline">
                <Text size="sm" className="font-mono mb-2">NOTE:</Text>
                <Text size="xs" className="text-neutral-900">
                  The lineage walkthrough is currently disabled. The question mark icon will not appear on the lineage tab. 
                  Use this section to navigate to recipe pages for testing when the walkthrough is re-enabled.
                </Text>
              </div>
            </div>

            {/* Walkthrough Status */}
            <div>
              <Heading level={4} className="text-neutral-900">Walkthrough Status</Heading>
              <div className="bg-neutral-50 p-4 hairline mb-4">
                <div className="space-y-2">
                  <div className="flex items-center justify-between">
                    <Text size="sm" className="font-mono text-neutral-900">Lineage Walkthrough:</Text>
                    <span className="px-3 py-1 font-mono text-xs bg-neutral-900 text-neutral-0">
                      DISABLED
                    </span>
                  </div>
                  <div className="h-px bg-neutral-900 opacity-20 my-2"></div>
                  <Text size="xs" className="text-neutral-900 opacity-80">
                    The question mark icon on the lineage tab is disabled. To re-enable, set the condition in FloatingLineageTab.jsx to show the TabQuestionMarkIcon.
                  </Text>
                </div>
              </div>
              
              <div className="bg-neutral-50 p-3 hairline">
                <Text size="sm" className="font-mono mb-2">TESTING INSTRUCTIONS:</Text>
                <ul className="list-disc list-inside space-y-1 text-xs text-neutral-900">
                  <li>Click a test button above to navigate to a recipe detail page</li>
                  <li>The lineage tab will be visible on the recipe page</li>
                  <li>The question mark icon will NOT appear (walkthrough disabled)</li>
                  <li>When ready to test, re-enable the walkthrough in FloatingLineageTab.jsx</li>
                </ul>
              </div>
            </div>
          </div>
        </div>

        {/* Animation Testing */}
        <div className="bg-neutral-0 hairline minimal-padding vspace mb-8">
          <Heading level={2} className="text-neutral-900" showInterLine>Animation Testing</Heading>
          
          <Text className="text-neutral-900 mb-4">
            Test the 4RKED title animation that appears on the Explore page. This animation shows once per session and includes floating cooking emojis and animated grid lines.
          </Text>
          
          <div className="bg-neutral-50 p-4 hairline">
            <Text size="sm" className="font-mono mb-2">ANIMATION FEATURES:</Text>
            <ul className="list-disc list-inside space-y-1 text-xs text-neutral-900 mb-4">
              <li>Session-based display (shows once per browser session)</li>
              <li>Animated grid lines with staggered timing</li>
              <li>Floating cooking emojis with rotation and scale</li>
              <li>Letter-by-letter title animation</li>
              <li>Smooth fade-out with slide-up effect</li>
              <li>1-hour expiration for repeat viewing</li>
            </ul>
            
            <button 
              onClick={() => {
                sessionStorage.removeItem('exploreAnimationShown');
                window.location.href = '/explore';
              }}
              className="px-4 py-2 bg-neutral-900 text-neutral-0 font-mono text-sm uppercase tracking-wider hover:opacity-60 transition-opacity"
            >
              TEST EXPLORE ANIMATION
            </button>
            
            <Text size="xs" className="text-neutral-900 opacity-60 mt-2 font-mono">
              Clears session storage and navigates to Explore page
            </Text>
          </div>
        </div>

        {/* Mock Elements for Walkthrough Testing */}
        {showWalkthrough && (
          <div className="fixed inset-0 bg-neutral-50 p-8 z-10">
            <div className="max-w-4xl mx-auto">
              {/* My Recipes Page */}
              {currentPage === 'recipes' && (
                <div className="bg-neutral-0 hairline minimal-padding vspace mb-8">
                  <div className="flex items-center justify-between mb-6">
                    <Heading level={2} className="text-neutral-900">My Recipes</Heading>
                    <button 
                      data-walkthrough="create-button"
                      onClick={() => setCurrentPage('create')}
                      className="px-4 py-2 bg-neutral-900 text-neutral-0 font-mono text-sm uppercase tracking-wider hover:opacity-60 transition-opacity"
                    >
                      Create
                    </button>
                  </div>
                  
                  <div className="text-center py-12">
                    <Text className="text-neutral-900 opacity-60">
                      No recipes yet. Click "Create" to get started!
                    </Text>
                  </div>
                </div>
              )}
              
              {/* Create Page */}
              {currentPage === 'create' && (
                <div className="bg-neutral-0 hairline minimal-padding vspace">
                  <div className="flex items-center justify-between mb-6">
                    <Heading level={3} className="text-neutral-900">Create New Recipe</Heading>
                    <button 
                      onClick={() => setCurrentPage('recipes')}
                      className="px-4 py-2 bg-neutral-50 text-neutral-900 hairline hover:bg-neutral-900 hover:text-neutral-0 transition-colors font-mono text-sm uppercase tracking-wider"
                    >
                      ← Back to Recipes
                    </button>
                  </div>
                  
                  <div className="space-y-6">
                    <div>
                      <Text className="text-neutral-900 mb-3">Smart Transfer</Text>
                      <div className="space-y-3">
                        <input 
                          data-walkthrough="smart-transfer"
                          type="text" 
                          placeholder="Paste recipe URL here..."
                          className="w-full p-3 hairline bg-neutral-0 text-neutral-900"
                        />
                        <button 
                          data-walkthrough="transfer-button"
                          className="px-4 py-2 bg-neutral-50 text-neutral-900 hairline hover:bg-neutral-900 hover:text-neutral-0 transition-colors font-mono text-sm uppercase tracking-wider"
                        >
                          Transfer
                        </button>
                      </div>
                    </div>
                    
                    <div className="h-px bg-neutral-900 opacity-20"></div>
                    
                    <div>
                      <Text className="text-neutral-900 mb-3">Recipe Details</Text>
                      <div className="space-y-3">
                        <input 
                          type="text" 
                          placeholder="Recipe Title"
                          className="w-full p-3 hairline bg-neutral-0 text-neutral-900"
                        />
                        <textarea 
                          placeholder="Recipe Description"
                          rows="4"
                          className="w-full p-3 hairline bg-neutral-0 text-neutral-900"
                        />
                      </div>
                    </div>
                    
                    <div className="flex justify-end">
                      <button 
                        data-walkthrough="create-recipe-button"
                        onClick={() => {
                          setCurrentPage('recipes');
                          // Simulate recipe creation completion
                          setTimeout(() => {
                            alert('Recipe created successfully! Walkthrough completed!');
                            setShowWalkthrough(false);
                          }, 1000);
                        }}
                        className="px-6 py-3 bg-neutral-900 text-neutral-0 font-mono text-sm uppercase tracking-wider hover:opacity-60 transition-opacity"
                      >
                        Create Recipe
                      </button>
                    </div>
                  </div>
                </div>
              )}
            </div>
          </div>
        )}

        {/* Walkthrough Manager */}
        <WalkthroughManager
          isActive={showWalkthrough}
          onComplete={() => {
            setShowWalkthrough(false);
            alert('Walkthrough completed! Great job!');
          }}
          onClose={() => setShowWalkthrough(false)}
        />

        {/* Popup Demo */}
        {showPopup && (
          <PopupWindow
            title="Style Guide Demo"
            images={[
              'https://images.unsplash.com/photo-1556909114-f6e7ad7d3136?w=400&h=400&fit=crop&crop=center',
              'https://images.unsplash.com/photo-1565299624946-b28f40a0ca4b?w=400&h=400&fit=crop&crop=center'
            ]}
            onClose={() => setShowPopup(false)}
            style={{ top: '120px', left: '120px' }}
          />
        )}
      </div>
    </div>
  );
};

export default StyleGuide; 